import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import debounce from 'lodash.debounce';

import { translationsSelector } from '../../../selectors/translations';

import Hyphenate from '../../../utils/hyphenate';
import { getBalanceWithCurrency } from '../../../constants/payment';
import { CustomIcon, Translation } from '../../common';

const CLAIM_TABLE_POSITION_KEY = 'CLAIM_TABLE_POSITION';
const CLAIM_TABLE_AMOUNT_KEY = 'CLAIM_TABLE_AMOUNT';

const EXPANDABLE_MAX_COLLAPSED_HEIGHT = 148;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative'
  },
  table: {
    transition: 'max-height 100ms linear',
    overflowY: 'auto'
  },
  expand: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translate(-50%, 50%)',
    padding: 10,
    background: theme.palette.primary.main,

    '&:hover': {
      background: theme.palette.primary.light
    }
  },
  expandOpen: {
    transform: 'translate(-50%, 50%) rotate(180deg)'
  },
  cell: {
    padding: 12,
    color: theme.palette.text.primary
  },
  headerCell: {
    ...theme.typography.body2Bold,
    background: theme.palette.action.disabled,
    borderBottom: `2px solid ${theme.palette.text.disabled}`
  },
  row: {
    background: theme.palette.background.default,
    cursor: 'pointer',

    '&:hover': {
      background: theme.palette.primary.light
    }
  },
  rowSelected: {
    background: theme.palette.primary.light
  },
  bodyCell: {
    ...theme.typography.subtitle1,
    borderBottom: `1px solid ${theme.palette.text.disabled}`
  }
}));

const useExpandableTable = (
  tableRef,
  tableWrapperRef,
  isExpandable,
  isExpanded,
  setIsExpanded
) => {
  const [
    isExpandableTableOverflowing,
    setIsExpandableTableOverflowing
  ] = useState(false);
  const [height, setHeight] = useState(0);

  const onResize = useCallback(() => {
    const height = tableRef.current.offsetHeight;
    const isOverflow = height > EXPANDABLE_MAX_COLLAPSED_HEIGHT;

    setIsExpandableTableOverflowing(isOverflow);
    setHeight(height);
    if (!isOverflow) {
      setIsExpanded(false);
    }
  }, [tableRef]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [tableRef, tableWrapperRef]);

  useEffect(() => {
    if (!isExpandable && isExpanded) {
      setIsExpanded(false);
    }
  }, [isExpandable, isExpanded, setIsExpanded]);

  const onExpandClick = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [setIsExpanded, isExpanded]);

  const expandableStyle = {
    maxHeight: `${isExpanded ? height : EXPANDABLE_MAX_COLLAPSED_HEIGHT}px`
  };

  return {
    isExpandableTableOverflowing,
    onExpandClick,
    expandableStyle
  };
};

// desktop version, table side needs to be at least same height to timeline
// if larger then table needs to scroll
const useScrollableTable = (
  isScrollable,
  translations,
  tableRef,
  demandTableContainerRef,
  timelineContainerRef,
  summaryRef,
  items,
  timelineItems
) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(100);

  const onResize = useCallback(() => {
    if (!isScrollable) {
      setHeight(100);
      return;
    }

    const demandTableContainer = demandTableContainerRef.current;
    const timelineContainer = timelineContainerRef.current;

    if (!summaryRef || !demandTableContainer || !timelineContainer) {
      return;
    }
    const summaryContainer = summaryRef.current;
    if (!summaryContainer) {
      return;
    }

    const contentHeight = tableRef.current.offsetHeight;
    const demandContainerHeight = demandTableContainer.offsetHeight;
    const demandContainerStyle = getComputedStyle(demandTableContainer);
    const demandPadding =
      parseFloat(demandContainerStyle.paddingTop) +
      parseFloat(demandContainerStyle.paddingBottom);

    let itemsHeight = 0;
    const children = Array.from(demandTableContainer.children);
    children.forEach(c => (itemsHeight += c.offsetHeight));

    const timelineContainerHeight = timelineContainer.offsetHeight;
    const summaryTop = parseFloat(getComputedStyle(summaryContainer).marginTop);

    const demandOverTimeline =
      demandContainerHeight - demandPadding - timelineContainerHeight;

    if (demandOverTimeline === 0) {
      if (summaryTop > 0) {
        const maxHeight = (height === 0 ? contentHeight : height) + summaryTop;
        if (maxHeight >= contentHeight) {
          setHeight(0);
        } else {
          setHeight(maxHeight);
        }
      }
    } else {
      let newHeight;
      if (height > 0) {
        newHeight = height - demandOverTimeline;
      } else {
        newHeight = contentHeight - demandOverTimeline;
      }
      setHeight(newHeight);
    }
  }, [
    tableRef,
    demandTableContainerRef,
    timelineContainerRef,
    summaryRef,
    height,
    setHeight,
    isScrollable,
    translations
  ]);

  useEffect(() => {
    onResize();
  }, [windowWidth]);

  useEffect(() => {
    debounce(onResize, 300)();
  }, [items, timelineItems]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    const debounced = debounce(handleResize, 100);
    window.addEventListener('resize', debounced);
    handleResize();
    return () => window.removeEventListener('resize', debounced);
  }, []);

  let scrollableStyle = {};
  if (height > 0) {
    scrollableStyle = {
      maxHeight: `${height}px`,
      overflowY: 'scroll'
    };
  }

  return {
    scrollableStyle
  };
};

const DemandTableContent = ({
  className,
  tableBodyClassName,
  isScrollable,
  isExpandable,
  isExpanded,
  setIsExpanded,
  items,
  selectedTableRow,
  setSelectedTableRow,
  demandTableContainerRef,
  timelineContainerRef,
  summaryRef,
  timelineItems
}) => {
  const classes = useStyles();
  const translations = useSelector(translationsSelector);
  const tableRef = useRef();
  const tableWrapperRef = useRef();

  const {
    isExpandableTableOverflowing,
    onExpandClick,
    expandableStyle
  } = useExpandableTable(
    tableRef,
    tableWrapperRef,
    isExpandable,
    isExpanded,
    setIsExpanded
  );

  const { scrollableStyle } = useScrollableTable(
    isScrollable,
    translations,
    tableRef,
    demandTableContainerRef,
    timelineContainerRef,
    summaryRef,
    items,
    timelineItems
  );

  const onSelectRow = index => () => {
    if (setSelectedTableRow) {
      if (selectedTableRow === index) {
        setSelectedTableRow(null);
      } else {
        setSelectedTableRow(index);
      }
    }
  };

  let style = {};

  if (isExpandable) {
    style = expandableStyle;
  } else if (isScrollable) {
    style = scrollableStyle;
  }

  return (
    <Box className={classNames(classes.root, className)}>
      <Box
        ref={tableWrapperRef}
        className={classNames(classes.table, tableBodyClassName)}
        style={style}
      >
        <Table
          stickyHeader
          ref={tableRef}
          aria-label="demand table"
          id="demand-table"
        >
          <TableHead>
            <TableRow>
              <TableCell
                className={classNames(classes.cell, classes.headerCell)}
              >
                <Translation inline>{CLAIM_TABLE_POSITION_KEY}</Translation>
              </TableCell>
              <TableCell
                className={classNames(classes.cell, classes.headerCell)}
                align="right"
              >
                <Translation inline>{CLAIM_TABLE_AMOUNT_KEY}</Translation>
              </TableCell>
            </TableRow>
          </TableHead>
          <ClickAwayListener onClickAway={onSelectRow(null)}>
            <TableBody>
              {items.map(({ amount, currency, subject }, index) => (
                <TableRow
                  className={classNames(
                    classes.row,
                    index === selectedTableRow && classes.rowSelected
                  )}
                  onClick={onSelectRow(index)}
                  key={index}
                >
                  <TableCell
                    className={classNames(classes.cell, classes.bodyCell)}
                  >
                    <Hyphenate>
                      <div>{subject}</div>
                    </Hyphenate>
                  </TableCell>
                  <TableCell
                    className={classNames(classes.cell, classes.bodyCell)}
                    align="right"
                  >
                    {getBalanceWithCurrency(amount, currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ClickAwayListener>
        </Table>
      </Box>
      {isExpandable && isExpandableTableOverflowing && (
        <IconButton
          aria-label="expand"
          className={classNames(
            classes.expand,
            isExpanded && classes.expandOpen
          )}
          onClick={onExpandClick}
        >
          <CustomIcon icon="expandMore" size="xss" variant="primaryContrast" />
        </IconButton>
      )}
    </Box>
  );
};

export default DemandTableContent;
